import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CreditCard, Upload, Camera, Trash2, Eye, FileText, Plus } from 'lucide-react';
import { toast } from 'sonner';

const TIPI_DOCUMENTO = [
  { value: 'carta_identita', label: 'Carta d\'Identita' },
  { value: 'codice_fiscale', label: 'Tessera Codice Fiscale' },
  { value: 'tessera_sanitaria', label: 'Tessera Sanitaria' },
  { value: 'patente', label: 'Patente' },
  { value: 'altro', label: 'Altro Documento' },
];

export default function DocumentiIdentitaSection({ ospiteId }) {
  const { hasRole } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [tipo, setTipo] = useState('carta_identita');
  const [preview, setPreview] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    try {
      const res = await api.get(`/ospiti/${ospiteId}/documenti-identita`);
      setDocs(res.data);
    } catch (e) { console.error('Errore caricamento documenti identita:', e); }
    setLoading(false);
  }, [ospiteId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File troppo grande (max 10MB)'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      await uploadDoc(file.name, reader.result);
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: { ideal: 1280 } } });
      streamRef.current = stream;
      setStreaming(true);
    } catch { toast.error('Impossibile accedere alla fotocamera'); }
  };

  // Assign stream to video element after it renders
  const videoCallbackRef = useCallback((node) => {
    videoRef.current = node;
    if (node && streamRef.current) {
      node.srcObject = streamRef.current;
      node.play().catch(() => {});
    }
  }, []);

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    stopCamera();
    uploadDoc(`documento_${tipo}_${Date.now()}.jpg`, dataUrl);
  };

  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setStreaming(false);
  };

  const uploadDoc = async (fileName, base64) => {
    setUploading(true);
    try {
      await api.post(`/ospiti/${ospiteId}/documenti-identita`, {
        tipo, nome_file: fileName, contenuto_base64: base64
      });
      toast.success('Documento caricato');
      fetchDocs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore upload'); }
    setUploading(false);
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo documento d\'identita?')) return;
    try {
      await api.delete(`/ospiti/${ospiteId}/documenti-identita/${docId}`);
      toast.success('Documento eliminato');
      fetchDocs();
    } catch (e) { toast.error('Errore eliminazione'); }
  };

  const handlePreview = async (docId) => {
    try {
      const res = await api.get(`/ospiti/${ospiteId}/documenti-identita/${docId}`);
      setPreview(res.data);
    } catch { toast.error('Errore caricamento anteprima'); }
  };

  const tipoLabel = (t) => TIPI_DOCUMENTO.find(d => d.value === t)?.label || t;

  return (
    <Card data-testid="documenti-identita-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-amber-600" />Documenti di Identita
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload area */}
        {hasRole('coordinatore', 'amministrazione', 'medico', 'infermiere') && (
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger className="w-48 h-8 text-xs" data-testid="tipo-documento-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TIPI_DOCUMENTO.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-doc-btn">
              <Upload className="w-3.5 h-3.5 mr-1" />{uploading ? 'Caricamento...' : 'Carica PDF/Foto'}
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={startCamera} disabled={streaming} data-testid="camera-doc-btn">
              <Camera className="w-3.5 h-3.5 mr-1" />Scatta Foto
            </Button>
            <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={handleFileUpload} />
          </div>
        )}

        {/* Camera capture */}
        {streaming && (
          <div className="relative rounded-lg overflow-hidden bg-black">
            <video ref={videoCallbackRef} className="w-full max-h-[200px] object-contain" autoPlay playsInline muted />
            <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2">
              <Button size="sm" onClick={capturePhoto} className="bg-white text-black hover:bg-slate-100" data-testid="capture-doc-btn">
                <Camera className="w-4 h-4 mr-1" />Scatta
              </Button>
              <Button size="sm" variant="ghost" className="text-white" onClick={stopCamera}>Annulla</Button>
            </div>
          </div>
        )}
        <canvas ref={canvasRef} className="hidden" />

        {/* Documents list */}
        {loading ? <p className="text-xs text-slate-400 text-center py-2">Caricamento...</p>
        : docs.length === 0 ? <p className="text-xs text-slate-400 text-center py-2">Nessun documento caricato</p>
        : (
          <div className="space-y-2">
            {docs.map(d => (
              <div key={d.id} className="flex items-center gap-3 p-2.5 rounded-lg border hover:bg-slate-50 transition-colors" data-testid={`doc-identita-${d.id}`}>
                <div className="w-8 h-8 rounded bg-amber-50 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-700 truncate">{d.nome_file}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant="outline" className="text-[10px]">{tipoLabel(d.tipo)}</Badge>
                    <span className="text-[10px] text-slate-400">{new Date(d.uploaded_at).toLocaleDateString('it-IT')}</span>
                    <span className="text-[10px] text-slate-400">da {d.uploaded_by}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handlePreview(d.id)} data-testid={`view-doc-${d.id}`}>
                    <Eye className="w-3.5 h-3.5 text-blue-500" />
                  </Button>
                  {hasRole('coordinatore', 'amministrazione') && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDelete(d.id)}>
                      <Trash2 className="w-3.5 h-3.5 text-red-400" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Preview dialog */}
        <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Anteprima Documento</DialogTitle></DialogHeader>
            {preview && (
              <div className="mt-2">
                <p className="text-sm font-medium mb-2">{tipoLabel(preview.tipo)} - {preview.nome_file}</p>
                {preview.contenuto_base64?.startsWith('data:application/pdf') ? (
                  <iframe src={preview.contenuto_base64} className="w-full h-[500px] rounded border" title="PDF Preview" />
                ) : preview.contenuto_base64?.startsWith('data:image') ? (
                  <img src={preview.contenuto_base64} alt="Documento" className="max-w-full rounded border" />
                ) : (
                  <p className="text-xs text-slate-400">Anteprima non disponibile per questo formato</p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        <p className="text-[10px] text-slate-400 leading-relaxed">
          I documenti di identita sono conservati ai sensi del D.Lgs. 196/2003 e del GDPR (Reg. UE 2016/679).
          L'accesso e riservato al personale autorizzato.
        </p>
      </CardContent>
    </Card>
  );
}
